/**
 * Gear: 6 slots (arma, anillo, casco, pecho, guantes, botas), tiers 1-6,
 * drop ponderado al terminar una run, fusión de 3 piezas iguales → 1 del
 * tier superior, equipamiento y cálculo de bonos totales sobre el RunState.
 */

import type { GearBonus, GearDef, GearPiece, GearSlot, MetaState, RunState } from '../core/types';
import { gear as gearDefs } from '../data';
import type { Rng } from '../core/rng';

/** Tier máximo alcanzable por cualquier pieza de gear. */
export const MAX_GEAR_TIER = 6;

/** Peso relativo de drop por tier (índice 0 = tier 1): a mayor tier, más raro. */
const TIER_DROP_WEIGHTS = [100, 55, 28, 13, 5, 2] as const;

function getDef(setKey: string, slot: GearSlot, defs: GearDef[] = gearDefs): GearDef {
  const def = defs.find((d) => d.setKey === setKey && d.slot === slot);
  if (!def) throw new Error(`gear: no existe definición para ${setKey}/${slot}`);
  return def;
}

function maxTierFor(def: GearDef): number {
  return Math.min(MAX_GEAR_TIER, def.rarityByTier.length, def.bonusByTier.length);
}

/** Elige un tier al azar (1-indexado) ponderando los tiers altos como más raros. */
function weightedTier(rng: Rng, maxTier: number): number {
  const weights = TIER_DROP_WEIGHTS.slice(0, maxTier);
  const total = weights.reduce((sum, w) => sum + w, 0);
  let roll = rng.float() * total;
  for (let i = 0; i < weights.length; i++) {
    roll -= weights[i] as number;
    if (roll <= 0) return i + 1;
  }
  return weights.length;
}

function randomGearId(rng: Rng, setKey: string, slot: GearSlot): string {
  return `${setKey}:${slot}:${rng.int(0, 0xffffff).toString(16)}:${rng.int(0, 0xffffff).toString(16)}`;
}

/** Genera una pieza de gear al azar entre todas las definiciones disponibles. */
export function rollGearDrop(rng: Rng, defs: GearDef[] = gearDefs): GearPiece {
  const def = rng.pick(defs);
  const tier = weightedTier(rng, maxTierFor(def));
  return { id: randomGearId(rng, def.setKey, def.slot), slot: def.slot, tier, setKey: def.setKey };
}

/** Genera varias piezas de gear de una sola vez (drop de fin de run). */
export function rollGearDrops(count: number, rng: Rng, defs: GearDef[] = gearDefs): GearPiece[] {
  return Array.from({ length: Math.max(0, count) }, () => rollGearDrop(rng, defs));
}

/** Un grupo de piezas idénticas (mismo set + slot + tier), útil para pintar la grilla del lobby. */
export interface GearGroup {
  setKey: string;
  slot: GearSlot;
  tier: number;
  pieces: GearPiece[];
}

/** Agrupa el inventario por set+slot+tier. */
export function groupGear(pieces: GearPiece[]): GearGroup[] {
  const map = new Map<string, GearGroup>();
  for (const piece of pieces) {
    const key = `${piece.setKey}|${piece.slot}|${piece.tier}`;
    const existing = map.get(key);
    if (existing) {
      existing.pieces.push(piece);
    } else {
      map.set(key, { setKey: piece.setKey, slot: piece.slot, tier: piece.tier, pieces: [piece] });
    }
  }
  return [...map.values()];
}

/** ¿Este grupo tiene ≥3 piezas y todavía puede subir de tier? */
export function canMergeGroup(group: Pick<GearGroup, 'setKey' | 'slot' | 'tier' | 'pieces'>, defs: GearDef[] = gearDefs): boolean {
  if (group.pieces.length < 3) return false;
  const def = getDef(group.setKey, group.slot, defs);
  return group.tier < maxTierFor(def);
}

/**
 * Fusiona 3 piezas iguales (mismo set+slot+tier) en 1 pieza de tier
 * superior. Si no hay 3 disponibles o el tier ya es el máximo, devuelve el
 * inventario sin cambios (misma referencia).
 */
export function mergeGear(
  pieces: GearPiece[],
  setKey: string,
  slot: GearSlot,
  tier: number,
  rng: Rng,
  defs: GearDef[] = gearDefs
): GearPiece[] {
  const matching = pieces.filter((p) => p.setKey === setKey && p.slot === slot && p.tier === tier);
  if (matching.length < 3) return pieces;

  const def = getDef(setKey, slot, defs);
  if (tier >= maxTierFor(def)) return pieces;

  const toConsume = matching.slice(0, 3);
  const consumedIds = new Set(toConsume.map((p) => p.id));
  const remaining = pieces.filter((p) => !consumedIds.has(p.id));
  const merged: GearPiece = { id: randomGearId(rng, setKey, slot), slot, setKey, tier: tier + 1 };
  return [...remaining, merged];
}

/** Equipa una pieza del inventario (reemplaza lo que hubiera en ese slot). No-op si el id no existe. */
export function equipGear(meta: MetaState, gearId: string): MetaState {
  const piece = meta.gear.find((g) => g.id === gearId);
  if (!piece) return meta;
  return { ...meta, equipped: { ...meta.equipped, [piece.slot]: gearId } };
}

/** Quita lo equipado en un slot (la pieza sigue en el inventario). No-op si el slot ya está vacío. */
export function unequipSlot(meta: MetaState, slot: GearSlot): MetaState {
  if (!meta.equipped[slot]) return meta;
  const equipped = { ...meta.equipped };
  delete equipped[slot];
  return { ...meta, equipped };
}

type BonusStat = GearBonus['stat'];

/** Suma los bonos de todas las piezas equipadas, agrupados por stat. */
export function computeEquippedBonuses(meta: MetaState, defs: GearDef[] = gearDefs): Partial<Record<BonusStat, number>> {
  const totals: Partial<Record<BonusStat, number>> = {};
  for (const slot of Object.keys(meta.equipped) as GearSlot[]) {
    const gearId = meta.equipped[slot];
    if (!gearId) continue;
    const piece = meta.gear.find((g) => g.id === gearId);
    if (!piece) continue;
    const def = defs.find((d) => d.setKey === piece.setKey && d.slot === piece.slot);
    const bonuses = def?.bonusByTier[piece.tier - 1] ?? [];
    for (const bonus of bonuses) {
      totals[bonus.stat] = (totals[bonus.stat] ?? 0) + bonus.add;
    }
  }
  return totals;
}

/** Aplica los bonos del gear equipado al estado inicial de una run nueva. */
export function applyGearToRunState(runState: RunState, meta: MetaState, defs: GearDef[] = gearDefs): RunState {
  const bonuses = computeEquippedBonuses(meta, defs);
  const stats = { ...runState.stats };
  let maxHp = runState.maxHp;
  for (const [stat, add] of Object.entries(bonuses) as [BonusStat, number][]) {
    if (stat === 'maxHpFlat') {
      maxHp += add;
    } else {
      stats[stat] += add;
    }
  }
  return { ...runState, stats, maxHp, hp: maxHp };
}
